import { F0Box, F0Button, F0Text } from '@factorialco/f0-react'
import { Input } from '@factorialco/f0-react/dist/experimental'
import './ContentF0.styles.css'
import { DocumentsDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  DocumentForPreviewDocument,
  DocumentPreviewContentDocument,
  DocumentsDataCollectionDocument,
  UpdateBasicInfoDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useState } from 'react'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import CustomFieldsForm from 'modules/custom_resources/components/CustomFields/CustomFieldsForm'
import Esignature from 'modules/documents/components/DocumentPreview/Content/Esignature'
import RestoreFromTrashBinBox from 'modules/documents/components/DocumentPreview/Content/Esignature/RestoreFromTrashBinBox'
import FillablePDF from 'modules/documents/components/DocumentPreview/Content/FillablePDF'
import FolderSelectorF0 from 'modules/documents/components/Folders/FolderSelectorF0'
import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import { isInTrashBin, isPendingToFill, isSignable } from 'modules/documents/lib/Documents'
import { isWritableEmployeeFolder } from 'modules/documents/lib/Folders'
import { splitFileName } from 'modules/documents/lib/files/filenameUtils'
import StatusBlockF0 from 'modules/integrations_primobox/components/StatusBlock/StatusBlockF0'

interface Props {
  document: Document
  saving?: boolean
  disableFilleableConfirm?: boolean
  loading?: boolean
}

const ContentF0 = ({ document, saving, disableFilleableConfirm, loading }: Props) => {
  const [updateBasicInfo] = useMutation(UpdateBasicInfoDocument, {
    extractorFn: (data) => data.documentsMutations.updateBasicInfoDocumentsDocument,
  })

  const space = document.space
  const employeeId = document.employee?.id
  const documentName = document.filename
  const { fileName: initialFileName, fileExtension } = splitFileName(documentName)
  const [fileNameValue, setFileNameValue] = useState(initialFileName)
  const [folderId, setFolderId] = useState<string | undefined>(document.folder?.id)
  const [isDirty, setIsDirty] = useState(false)

  const { data, loading: loadingDocumentPreview } = useQuery(DocumentPreviewContentDocument, {
    variables: {
      companyId: document.company?.id ?? '',
      documentId: document.id,
      space,
      employeeIds: employeeId ? [employeeId] : [],
    },
    skip: !document.company?.id,
  })

  const employee = data?.documents.documentsConnection.nodes[0]?.employee
  const customResourceFieldsData =
    compact(
      data?.documents.folders.find((folder) => folder.id === document.folder?.id)?.resourceFields ??
        []
    ) ?? []
  const customValuesData = data?.documents.documentsConnection.nodes[0]?.values ?? []
  const signatureRequests = data?.esignature.requestsConnection.nodes ?? []
  const folders = data?.documents.folders ?? []
  const features = data?.features.featuresConnection.nodes ?? []
  const primoboxDocumentStatus = data?.integrationsPrimobox.documentStatusesConnection?.nodes[0]

  const hasFeature = (featureName: string) =>
    features.find((feature) => feature.name === featureName)?.enabled

  const currentFolder = folders.find((folder) => folder.id === document.folder?.id)

  const isCurrentUserASignee = !!document.signees?.find(
    (access_id) => access_id === employee?.access?.id
  )

  const canEditEmployeeDocumentsWithoutFolderForEverybody =
    data?.documents._policies?.withoutFolderEverybodyEditEmployeeDocumentsWithoutFolder?.can ??
    false
  const canEditEmployeeFolderForEverybody =
    data?.documents?.editableEmployeeFoldersConnection?.nodes.find(
      (f) => f.id === document.folder?.id
    )?._policies?.editEmployeeFolderForEverybody?.can ?? false
  const canEditEmployeeDocumentsForEverybody = document.folder?.id
    ? canEditEmployeeFolderForEverybody
    : canEditEmployeeDocumentsWithoutFolderForEverybody
  const canCompanyUseCompanyDocuments =
    data?.documents._policies?.companyUseCompanyDocuments.can ?? false

  const canUserSeeSignatures =
    canCompanyUseCompanyDocuments ||
    canEditEmployeeDocumentsForEverybody ||
    isCurrentUserASignee ||
    signatureRequests.length > 0

  const showEsignature = isSignable(document) && canUserSeeSignatures && !isPendingToFill(document)

  const canDelete = document._policies?.delete?.can ?? false
  const showRestoreDocument = isInTrashBin(document) && canDelete
  const showDocumentFields = !isPendingToFill(document)
  const showCustomFields = document.folder?.id && !isPendingToFill(document)
  const showFillableStatus = isPendingToFill(document)
  const showPrimobox = hasFeature('integrations_primobox_active')

  const canEdit = (): boolean => {
    return (
      (document._policies?.edit.can ?? false) &&
      !document.folder?.showOnly &&
      !isInTrashBin(document)
    )
  }

  const writableFolders = employee
    ? folders.filter((folder) =>
        isWritableEmployeeFolder(
          folder,
          data?.documents.documentsConnection.nodes[0]?.employee?._policies?.seeDocuments?.can ??
            false
        )
      )
    : folders

  const onSubmit = async () => {
    const fullFilename = `${fileNameValue}${fileExtension}`
    const submittedFolderId = folderId || undefined

    const { success } = await asyncAction(() => {
      return updateBasicInfo({
        variables: {
          id: document.id,
          filename: fullFilename,
          folderId: submittedFolderId,
        },
        refetchQueries: [
          DocumentsDataTableDocument,
          DocumentForPreviewDocument,
          DocumentsDataCollectionDocument,
        ],
      })
    })

    if (success) {
      setIsDirty(false)
    }
  }

  const isLoading = loading || loadingDocumentPreview

  if (isLoading)
    return (
      <F0Box
        display="flex"
        flexDirection="column"
        paddingY="lg"
        paddingX="md"
        gap="xl"
        width="full"
      >
        <F0Box gap="lg">
          <div className="ContentF0_skeleton ContentF0_skeleton--sm" />
          <div className="ContentF0_skeleton ContentF0_skeleton--lg" />
        </F0Box>
        <F0Box gap="lg">
          <div className="ContentF0_skeleton ContentF0_skeleton--sm" />
          <div className="ContentF0_skeleton ContentF0_skeleton--lg" />
        </F0Box>
      </F0Box>
    )

  return (
    <F0Box display="flex" flexDirection="column" paddingY="lg" paddingX="md" gap="xl" width="full">
      {showRestoreDocument && (
        <F0Box marginBottom="md">
          <RestoreFromTrashBinBox
            documentId={document.id}
            documentDeletedAt={document.deletedAt}
            useF0
          />
        </F0Box>
      )}
      {showDocumentFields && (
        <F0Box display="flex" flexDirection="column" gap="lg" width="full" paddingBottom="lg">
          <Input
            label={i18n.t('documents.modal.file_name')}
            value={fileNameValue}
            onChange={(value) => {
              setFileNameValue(value)
              setIsDirty(true)
            }}
            disabled={!canEdit()}
            size="sm"
          />
          {canEdit() ? (
            <FolderSelectorF0
              folders={writableFolders}
              {...(currentFolder ? { initialFolder: currentFolder } : {})}
              label={i18n.t('split_documents.form.select_label')}
              onChange={(value) => {
                setFolderId(value ?? undefined)
                setIsDirty(true)
              }}
            />
          ) : (
            <Input
              label={i18n.t('split_documents.form.select_label')}
              value={currentFolder?.name ?? i18n.t('split_documents.form.none')}
              onChange={() => {}}
              disabled
              size="sm"
            />
          )}
          {canEdit() && isDirty && (
            <F0Box display="flex" justifyContent="end">
              <F0Button label={i18n.t('common.submit')} onClick={onSubmit} />
            </F0Box>
          )}
        </F0Box>
      )}
      {showDocumentFields && <F0Box marginX="xl" borderBottom="default" borderColor="secondary" />}
      {showFillableStatus && (
        <F0Box paddingBottom="lg">
          <FillablePDF
            saving={!!saving}
            disableFilleableConfirm={!!disableFilleableConfirm}
            document={document}
            signatureRequests={signatureRequests}
            useF0
          />
        </F0Box>
      )}
      {showFillableStatus && <F0Box marginX="xl" borderBottom="default" borderColor="secondary" />}
      {showEsignature && (
        <F0Box paddingBottom="lg">
          <Esignature document={document} useF0 />
        </F0Box>
      )}
      {showEsignature && <F0Box marginX="xl" borderBottom="default" borderColor="secondary" />}
      {showPrimobox && primoboxDocumentStatus && (
        <F0Box display="flex" flexDirection="column" gap="md" paddingY="lg">
          <F0Box display="flex" flexDirection="column" gap="xs">
            <F0Text
              variant="body"
              content={i18n.t('integrations_primobox.legal_entity_status.title')}
            />
            <F0Text
              variant="description"
              content={i18n.t('integrations_primobox.legal_entity_status.description')}
            />
          </F0Box>
          <StatusBlockF0 status={primoboxDocumentStatus} inModal />
        </F0Box>
      )}
      {showPrimobox && primoboxDocumentStatus && (
        <F0Box marginX="xl" borderBottom="default" borderColor="secondary" />
      )}

      {showCustomFields && (
        <F0Box display="flex" flexDirection="column" gap="md" paddingY="lg">
          <F0Box display="flex" flexDirection="column" gap="xs">
            <F0Text variant="body" content={i18n.t('documents.custom_fields.title')} />
            <F0Text variant="description" content={i18n.t('documents.custom_fields.description')} />
          </F0Box>
          <CustomFieldsForm
            padFormDirection="vertical"
            boxed={false}
            customFieldsOnly
            customResourceFieldsData={customResourceFieldsData}
            customValuesData={customValuesData}
            valuableId={document.id}
            valuableType="Document"
            currency={employee?.legalEntity?.currency}
          />
        </F0Box>
      )}
    </F0Box>
  )
}

export default ContentF0
